class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItem = this.closest('.cart-item');
      // calls the assisted-wheels-upgrade component to update the quantity
      const wheelUpgradeVariantId = cartItem?.querySelector('assisted-wheels-upgrade')?.variantId;
      const cartDrawer = this.closest('cart-drawer');
      const variantToRemove = this.dataset.variant;
      
      // Add loading state
      if (cartItem) {
        cartItem.classList.add('cart-item--updating');
      }
      
      setTimeout(async () => {
        const cartResponse = await fetch('/cart.js', { cache: 'no-store' });
        const cart = await cartResponse.json();

        // Travel System lines: removing the wagon / adapter also removes the
        // rest of its bundle (see getTravelSystemUpdates in global.js).
        const lineItem = typeof window.findTravelSystemLine === 'function'
          ? window.findTravelSystemLine(cart, this.dataset)
          : null;
        const travelSystemUpdates = lineItem && typeof window.getTravelSystemUpdates === 'function'
          ? window.getTravelSystemUpdates(cart, lineItem.key, 0)
          : null;

        if (travelSystemUpdates) {
          await window.applyTravelSystemUpdates(travelSystemUpdates, cart);
        } else {
          // Line KEYS, not 1-based indexes: /cart/change.js reorders the cart,
          // so a second index computed here would point at the wrong line after
          // the first gift is removed. A gated parent can have more than one
          // _free_gift line (main companion + metafield e-wheels upsell).
          const parentItem = cart.items.find((item) =>
            String(item.variant_id) === String(variantToRemove) &&
            !(item.properties && item.properties._free_gift === 'true')
          );
          const freeGiftKeys = [];
          cart.items.forEach((item) => {
            if (item.properties &&
                item.properties._free_gift === 'true' &&
                String(item.properties._parent_variant_id) === String(variantToRemove)) {
              freeGiftKeys.push(item.key);
            }
          });

          for (const key of freeGiftKeys) {
            await fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              ...{
                body: JSON.stringify({
                  id: key,
                  quantity: 0
                })
              }
            });
          }

          // Remove wheel upgrade if exists
          if (wheelUpgradeVariantId) {
            await fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              ...{
                body: JSON.stringify({
                  id: String(wheelUpgradeVariantId),
                  quantity: 0
                })
              }
            });
          }

          await fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            ...{
              body: JSON.stringify({
                id: parentItem ? parentItem.key : String(variantToRemove),
                quantity: 0
              })
            }
          });
        }

        // Refresh sections once at the end
        const finalResponse = await fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: {},
            sections: cartDrawer.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname
          })
        });
        const finalState = await finalResponse.json();
        
        cartDrawer.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            
          elementToReplace.innerHTML =
            cartDrawer.getSectionInnerHTML(finalState.sections[section.section], section.selector);
        }));
        
        window.updateSubtotalPrice();

        // Removing the capped preorder line frees up the Stoq cap — re-enable
        // the PDP ATC without a reload.
        if (typeof window.syncStoqPreorderCapState === 'function') {
          window.syncStoqPreorderCapState(finalState);
        }

        // Same event updateQuantity emits — lets open quick views revert a
        // capped "Out of Stock" ATC back to Preorder without a reload.
        document.dispatchEvent(new CustomEvent('cart:ajax-updated', {
          detail: { line: variantToRemove, quantity: 0, parsedState: finalState, sections: cartDrawer.getSectionsToRender() }
        }));
      }, 1000);
    });
  }
}

customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.body = document.body;
    this.drawer = document.getElementById('cart-drawer');
    
    this.cartButton = document.querySelector('.js-open-cart');
    this.cartButton.addEventListener('click', this.handleCartClick.bind(this));

    this.closeIcons = document.querySelectorAll('.cart-drawer__close');
    this.closeIcons.forEach(closeIcon => {
      closeIcon.addEventListener('click', this.close.bind(this));
    });
    
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.drawer.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.pageOverlayElement = document.querySelector('.page-overlay');

    // Functionality
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      if (!event.target.classList.contains('cart_upsell-variant-select')) {
        this.onChange(event);
      }
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    this.redoRefreshTimeout = null;
    this.addEventListener('click', this.handleRedoToggleClick.bind(this));

    // Checkout navigation takes a beat (Shopify bounces /checkout through
    // shop.app when Shop Pay universal redirect is on), during which Chrome
    // flickers the cursor between pointer and default. Hold a steady wait
    // cursor on the button instead. Listener lives on the form (not the
    // button) because the footer is inside .cart-drawer__content, whose
    // innerHTML is replaced on cart updates.
    this.form = document.getElementById('cart-drawer__form');
    this.form?.addEventListener('submit', (event) => {
      const checkoutButton =
        event.submitter?.name === 'checkout'
          ? event.submitter
          : this.form.querySelector('.cart-drawer__checkout');
      if (!checkoutButton) return;
      checkoutButton.classList.add('cart-drawer__checkout--waiting');
      // Disable AFTER the form data is serialized (next tick): disabling
      // synchronously here would drop name="checkout" from the POST body and
      // Shopify would reload the cart page instead of opening checkout.
      setTimeout(() => {
        checkoutButton.disabled = true;
      }, 0);
    });
  }

  handleRedoToggleClick(event) {
    const redoToggle =
      event.target.closest?.('redo-shopify-toggle') ||
      (event.target.tagName === 'REDO-SHOPIFY-TOGGLE' ? event.target : null);

    if (!redoToggle) return;

    clearTimeout(this.redoRefreshTimeout);
    this.redoRefreshTimeout = setTimeout(() => {
      this.refreshAfterRedoToggle();
    }, 600);
  }

  async refreshAfterRedoToggle() {
    const cartBefore = await fetch('/cart.js').then((response) => response.json());
    const signature = (cart) =>
      JSON.stringify({
        item_count: cart.item_count,
        total_price: cart.total_price,
        items: cart.items.map((item) => `${item.id}:${item.quantity}`),
      });

    const beforeSignature = signature(cartBefore);
    const maxAttempts = 15;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      const cartAfter = await fetch('/cart.js').then((response) => response.json());
      if (signature(cartAfter) !== beforeSignature) {
        await this.refreshSections();
        return;
      }
    }

    await this.refreshSections();
  }

  async refreshSections() {
    try {
      const response = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates: {},
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) ||
          document.getElementById(section.id);

        elementToReplace.innerHTML = this.getSectionInnerHTML(
          state.sections[section.section],
          section.selector
        );
      });

      window.updateSubtotalPrice?.();

      document.dispatchEvent(
        new CustomEvent('cart:ajax-updated', {
          detail: { parsedState: state, sections: this.getSectionsToRender(), source: 'redo-toggle' },
        })
      );
    } catch (error) {
      // Redo toggle refresh failed silently; drawer state stays as-is
    }
  }

  open() {
    this.drawer.setAttribute('aria-hidden', false);
    this.drawer.setAttribute('aria-expanded', true);

    this.pageOverlayElement.classList.add('is-visible');
    document.body.addEventListener('click', this.onBodyClick);

    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    this.body.style.top = `-${this.scrollPosition}px`;
    this.body.classList.add('scroll-lock');
    
    setTimeout(function(){
      window.updateSubtotalPrice();
    },500);
  }

  close(event) {
    if (event) {
      event.preventDefault();
    }

    this.drawer.setAttribute('aria-hidden', true);
    this.drawer.removeAttribute('aria-expanded', true);

    this.pageOverlayElement.classList.remove('is-visible');
    document.body.removeEventListener('click', this.onBodyClick);

    this.body.classList.remove('scroll-lock');
    this.body.style.top = '';
    
    if (this.scrollPosition !== undefined) {
      const previousHtmlScrollBehavior = document.documentElement.style.scrollBehavior;
      const previousBodyScrollBehavior = this.body.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      this.body.style.scrollBehavior = 'auto';
      window.scrollTo(0, this.scrollPosition);
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = previousHtmlScrollBehavior;
        this.body.style.scrollBehavior = previousBodyScrollBehavior;
      }, 0);
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.variant, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset);
  }

  async updateQuantity(variant, quantity, name, lineData) {
    this.enableLoading(variant);

    const cartResponse = await fetch('/cart.js', { cache: 'no-store' });
    const cart = await cartResponse.json();

    // Travel System lines move as a bundle (see getTravelSystemUpdates in global.js).
    const lineItem = typeof window.findTravelSystemLine === 'function'
      ? window.findTravelSystemLine(cart, lineData)
      : null;
    const travelSystemUpdates = lineItem && typeof window.getTravelSystemUpdates === 'function'
      ? window.getTravelSystemUpdates(cart, lineItem.key, quantity)
      : null;

    // Companion lines are addressed by line KEY, not index: /cart/change.js
    // REORDERS the cart, so an index computed here would point at the wrong
    // line after the parent's own change request below.
    const freeGiftLines = [];
    cart.items.forEach((item) => {
      if (item.properties && 
          item.properties._free_gift === 'true' && 
          String(item.properties._parent_variant_id) === String(variant)) {
        freeGiftLines.push({ key: item.key, variantId: item.variant_id });
      }
    });

    // Custom enforcement of the Stoq preorder unit cap: clamp the line itself
    // to _StoqConfig.variantsPreorderMaxCount (the app used to revert it only
    // on its own, after the fact). Companion (_free_gift) lines follow the
    // same clamped quantity. No cap (NaN / app absent) = no limit.
    const parentCap = typeof window.getStoqPreorderLimit === 'function'
      ? window.getStoqPreorderLimit(variant)
      : null;
    const targetQuantity = parentCap ? Math.min(parseInt(quantity, 10) || 0, parentCap) : quantity;

    const body = JSON.stringify({
      id: variant,
      quantity: targetQuantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    try {
      if (travelSystemUpdates) {
        await window.applyTravelSystemUpdates(travelSystemUpdates, cart);
      } else {
        const response = await fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }});
        const state = await response.text();
        const parsedState = JSON.parse(state);

        for (const gift of freeGiftLines) {
          // Companion follows the parent's quantity but is also clamped by its
          // OWN cap (customizer "Max per cart — Companion product" / Stoq).
          const giftCap = typeof window.getStoqPreorderLimit === 'function'
            ? window.getStoqPreorderLimit(gift.variantId)
            : null;
          const giftQuantity = giftCap
            ? Math.min(parseInt(targetQuantity, 10), giftCap)
            : parseInt(targetQuantity, 10);
          await fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            ...{
              body: JSON.stringify({
                id: gift.key,
                quantity: giftQuantity,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname
              })
            }
          });
        }
      }

      const finalResponse = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          updates: {},
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      });
      const finalState = await finalResponse.json();

      this.getSectionsToRender().forEach((section => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          
        elementToReplace.innerHTML =
          this.getSectionInnerHTML(finalState.sections[section.section], section.selector);

      }));
    
      window.updateSubtotalPrice();

      // Re-check the Stoq preorder cap so the PDP ATC flips between
      // "Out of Stock" and preorder in real time as cart quantities change.
      if (typeof window.syncStoqPreorderCapState === 'function') {
        window.syncStoqPreorderCapState(finalState);
      }

      // Emit global cart update event after any AJAX cart operation
      document.dispatchEvent(new CustomEvent('cart:ajax-updated', {
        detail: { line: variant, quantity, parsedState: finalState, sections: this.getSectionsToRender() }
      }));

      this.disableLoading();
    } catch (error) {
      this.disableLoading();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer__content',
        section: document.getElementById('cart-drawer__content').dataset.id,
        selector: '.cart-drawer__content',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading() {
    // document.getElementById('cart-drawer-loading').classList.remove('hidden');
  }

  disableLoading() {
    // document.getElementById('cart-drawer-loading').classList.add('hidden');
  }

  handleBodyClick(evt) {
    const target = evt.target;
    // console.log(evt)
    if (target.classList.contains('page-overlay')) {
      this.close();
    }
  }

  handleCartClick(evt) {
    evt.preventDefault();
    this.open();
  }
}

customElements.define('cart-drawer', CartDrawer);

/*
  Bazaarvoice's inline_ratings app only scans for star containers once on initial
  page load (it has no MutationObserver). The drawer's line items are re-rendered
  via the Section Rendering API (quantity change, remove, add to cart, redo toggle),
  which replaces the [data-bv-show="inline_rating"] divs with fresh empty ones that
  BV never picks up. Re-trigger BV's own "container_added" render pipeline for any
  star container added to the drawer after page load.
*/
(function () {
  function renderInlineRatings(root) {
    const bvApp = window.BV && window.BV.inline_ratings;
    if (!bvApp || typeof bvApp.trigger !== 'function') return;
    root.querySelectorAll('[data-bv-show="inline_rating"]').forEach((container) => {
      if (!container.firstElementChild) {
        bvApp.trigger('container_added', container);
      }
    });
  }

  function init() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || !('MutationObserver' in window)) return;

    let scheduled = null;
    const observer = new MutationObserver(() => {
      clearTimeout(scheduled);
      scheduled = setTimeout(() => renderInlineRatings(drawer), 50);
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
